import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient } from "@/lib/apiClient";
import { useDebounce } from "@/hooks/useDebounce";

interface CityItem {
  id: number;
  nome: string;
  codigoIbge?: number | null;
  estado?: { uf: string } | null;
}
interface CitiesResp { itens: CityItem[] }

interface Props {
  value?:       string | null;   // nome da cidade exibido
  onSelect:     (city: { nome: string; codigoIbge: number | null; uf: string | null }) => void;
  placeholder?: string;
  disabled?:    boolean;
}

export function CitySearchCombobox({ value, onSelect, placeholder = "Buscar cidade...", disabled }: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);

  const { data, isFetching } = useQuery({
    queryKey: ["city-combo", debounced],
    queryFn:  async () => {
      const { data } = await apiClient.get<CitiesResp>("/api/cidades", {
        params: { ...(debounced ? { busca: debounced } : {}), pagina: 1, tamanhoPagina: 40 },
      });
      return data.itens;
    },
    enabled:  open,
    staleTime: 60_000,
  });
  const results = data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          type="button"
        >
          <span className={cn("truncate text-left", !value && "text-muted-foreground")}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Digite o nome da cidade..." value={search} onValueChange={setSearch} />
          <CommandList>
            {isFetching && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isFetching && <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>}
            <CommandGroup>
              {results.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.nome}-${c.id}`}
                  onSelect={() => {
                    onSelect({ nome: c.nome, codigoIbge: c.codigoIbge ?? null, uf: c.estado?.uf ?? null });
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === c.nome ? "opacity-100" : "opacity-0")} />
                  <span className="text-sm">{c.nome}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{c.estado?.uf}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
